<template>
  <div class="chat-container">
    <!-- 添加图片预览弹窗 -->
    <el-dialog
      :visible.sync="showImagePreview"
      :append-to-body="true"
      custom-class="image-preview-dialog"
      :show-close="true"
      :modal="true"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      width="auto"
    >
      <img :src="uploadedImage" alt="preview" class="preview-image" />
    </el-dialog>

    <!-- 左侧历史记录 -->
    <div class="history-panel" :class="{ 'history-hidden': !showHistory }">
      <div class="history-header">
        <span>历史记录</span>
        <i class="el-icon-d-arrow-left" @click="toggleHistory"></i>
      </div>
      <div
        class="history-list"
        ref="historyListRef"
        @scroll="handleHistoryScroll"
      >
        <!-- 首次加载状态 -->
        <div v-if="isFirstLoad" class="loading-more">
          <i class="el-icon-loading"></i>
          加载中...
        </div>

        <!-- 空状态 -->
        <div v-else-if="!historyList.length && !loading" class="empty-history">
          <i class="el-icon-chat-dot-square"></i>
          <p>暂无历史记录</p>
        </div>

        <!-- 历史记录列表 -->
        <template v-else>
          <!-- 使用虚拟列表优化渲染性能 -->
          <div class="history-items-container">
            <div
              v-for="(item, index) in historyList"
              :key="item.id || index"
              class="history-item"
              :class="{
                'history-item-active': selectedHistoryId === item.id,
                deleting: deletingItems.has(item.id),
              }"
              :style="{
                'animation-delay': `${Math.min(index, 10) * 0.05}s`,
                'will-change': 'transform, opacity',
              }"
              @click="selectHistory(item)"
            >
              <div class="history-logo">
                <img src="@/assets/bot-avatar.png" alt="logo" />
              </div>
              <div class="history-content">
                {{ item.content }}
              </div>
              <i
                class="el-icon-delete delete-icon"
                :class="{ deleting: deletingItems.has(item.id) }"
                @click.stop="deleteHistory(item)"
              ></i>
            </div>
          </div>

          <!-- 加载状态区域 -->
          <div class="loading-status-container">
            <!-- 加载更多状态 -->
            <div v-if="loading" class="loading-more">
              <i class="el-icon-loading"></i>
              加载中...
            </div>

            <!-- 加载错误状态 -->
            <div v-if="loadingError" class="loading-more error">
              <i class="el-icon-warning"></i>
              加载失败，点击
              <el-button type="text" @click="getHistoryList(true)"
                >重试</el-button
              >
            </div>

            <!-- 没有更多数据状态 -->
            <div v-if="!hasMore && !loading" class="no-more">
              没有更多数据了
            </div>
          </div>
        </template>
      </div>
      <div class="new-chat" @click="startNewChat">
        <i class="el-icon-plus"></i>
        开始新对话
      </div>
    </div>

    <!-- 右侧聊天区域 -->
    <div class="chat-main" :class="{ 'full-width': !showHistory }">
      <!-- 顶部控制栏 -->
      <div class="chat-header">
        <div class="header-left">
          <el-button
            v-if="!showHistory"
            type="text"
            class="toggle-history-btn"
            @click="toggleHistory"
          >
            <i class="el-icon-d-arrow-right"></i>
            显示历史记录
          </el-button>
        </div>
        <div class="header-right">
          <el-switch
            v-model="currentMode"
            active-text="知识库模式"
            inactive-text="AI对话模式"
            active-value="knowledge"
            inactive-value="deepseek"
            :disabled="isAiResponding"
            @change="handleModeChange"
          >
          </el-switch>
        </div>
      </div>

      <!-- 欢迎页面 -->
      <div v-if="!messages.length" class="welcome-page">
        <div class="welcome-content">
          <img src="@/assets/bot-avatar.png" alt="logo" class="logo" />
          <h1>我是 <span class="highlight">融小跃</span>, 很高兴见到你！</h1>
          <p class="description">
            帮你解决备考难题【精准答疑助你高效提升学习效率】
          </p>
        </div>
      </div>

      <!-- 聊天内容区域 -->
      <div
        v-else
        class="chat-messages"
        ref="messageContainer"
        @scroll="handleMessagesScroll"
      >
        <div
          v-for="(msg, index) in messages"
          :key="index"
          :class="['message-item', msg.type]"
        >
          <div class="avatar">
            <img
              :src="msg.type === 'user' ? userAvatar : botAvatar"
              alt="avatar"
            />
          </div>
          <div class="message-content">
            <!-- 添加图片显示区域 -->
            <div
              v-if="msg.type === 'user' && msg.hasImage"
              class="message-image"
            >
              <img
                :src="msg.image"
                alt="sent image"
                @click="handleImageClick(msg.image)"
                class="sent-image"
              />
            </div>
            <div
              v-if="msg.thinking"
              class="thinking-content"
              v-html="msg.formattedThinking"
            ></div>
            <div
              v-if="msg.content"
              class="answer-content"
              :class="{ error: msg.status === 'error' }"
              v-html="msg.formattedContent"
            ></div>
          </div>
        </div>
      </div>

      <!-- 输入框区域 -->
      <div class="input-area">
        <div class="textarea-wrapper">
          <div class="preview-area" v-if="uploadedImage">
            <div class="image-preview" @click="handlePreviewClick">
              <img :src="uploadedImage" alt="uploaded preview" />
              <div class="remove-image" @click.stop="removeUploadedImage">
                <i class="el-icon-close"></i>
              </div>
            </div>
          </div>
          <el-input
            v-model="inputMessage"
            type="textarea"
            :rows="3"
            placeholder="输入消息..."
            @keydown.native="handleKeyDown"
          ></el-input>
          <div class="input-actions">
            <el-upload
              class="upload-image"
              action="#"
              :show-file-list="false"
              :http-request="handleImageUpload"
            >
              <el-button type="text">
                <i class="el-icon-picture"></i>
              </el-button>
            </el-upload>
            <div class="action-divider"></div>
            <el-button
              v-if="!isAiResponding"
              type="text"
              class="send-button"
              @click="sendMessage"
              :disabled="!inputMessage.trim()"
            >
              <i class="el-icon-s-promotion"></i>
            </el-button>
            <el-button
              type="text"
              class="stop-button"
              @click="stopAiResponse"
              v-else
            >
              <i class="el-icon-circle-close"></i>
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { chatApi } from "@/api";
import { marked } from "marked";
import hljs from "highlight.js";
import "highlight.js/styles/atom-one-dark.css";
import { disableZoom } from "@/utils/disableZoom";

// 配置 marked
const renderer = new marked.Renderer();

// 自定义列表渲染
renderer.list = function (body, ordered) {
  const type = ordered ? "ol" : "ul";
  return `<${type} class="custom-list">${body}</${type}>`;
};

// 自定义列表项渲染
renderer.listitem = function (text) {
  return `<li class="custom-list-item">${text}</li>`;
};

// 自定义段落渲染
renderer.paragraph = function (text) {
  return `<p class="custom-paragraph">${text}</p>`;
};

// 自定义标题渲染
renderer.heading = function (text, level) {
  return `<h${level} class="custom-heading custom-heading-${level}">${text}</h${level}>`;
};
// function getCookie(name) {
//   const value = `; ${document.cookie}`;
//   const parts = value.split(`; ${name}=`);
//   if (parts.length === 2) return parts.pop().split(";").shift();
//   return null;
// }
function cookies(t) {
  var cookie = document.cookie,
    arrcookie = cookie.split("; ");
  for (var i = 0; i < arrcookie.length; i++) {
    var arr = arrcookie[i].split("=");
    if (arr[0] == t) {
      return arr[1];
    }
  }
}
marked.setOptions({
  renderer,
  highlight: (code) => {
    return hljs.highlightAuto(code).value;
  },
  gfm: true,
  breaks: true,
});

export default {
  name: "ai-deepseek",
  data() {
    return {
      showHistory: this.isDesktop(),
      messages: [],
      currentMessage: null,
      inputMessage: "",
      historyList: [],
      userAvatar: require("@/assets/user-avatar.png"),
      botAvatar: require("@/assets/bot-avatar.png"),
      streamQueue: [], // 用于存储待处理的流式内容
      isProcessingStream: false, // 标记是否正在处理流式内容
      isAiResponding: false, // 标记AI是否正在响应
      shouldStopResponse: false, // 控制是否停止响应
      abortController: null, // 用于中断请求的控制器
      currentMode: "deepseek", // 当前模式：deepseek 或 knowledge
      newDialogSessonId: "", //新创建会话的数据
      // 新增分页相关数据
      pageNum: 1,
      pageSize: 20,
      loading: false,
      hasMore: true,
      selectedHistoryId: null, // 新增选中项的ID
      // 历史记录详情分页相关数据
      historyDetailPageNum: 1,
      historyDetailPageSize: 6,
      historyDetailLoading: false,
      historyDetailHasMore: true,
      currentHistorySession: "", // 当前查看的历史会话ID
      scrollThrottleTimer: null, // 添加滚动节流定时器
      isFirstLoad: true, // 新增首次加载状态
      loadingError: false, // 新增加载错误状态
      uploadedImage: null, // 新增：存储上传的图片URL
      showImagePreview: false, // 新增：控制图片预览弹窗的显示
      send_image_name: null, // 新增：存储上传的图片名称
      deletingItems: new Set(), // 新增：用于跟踪正在删除的项
      isAppEnvironment: false, // 新增：标记是否在APP环境中
    };
  },
  methods: {
    // 修改：获取URL参数的方法，支持Hash路由
    getUrlParam(name) {
      try {
        const url = window.location.href;
        const hashIndex = url.indexOf('#');
        if (hashIndex > -1) {
          // 如果URL中包含#，则从#后面的部分获取参数
          const queryString = url.slice(hashIndex).split('?')[1];
          if (queryString) {
            const params = new URLSearchParams(queryString);
            return params.get(name);
          }
        } else {
          // 如果URL中不包含#，则直接获取参数
          const params = new URLSearchParams(window.location.search);
          return params.get(name);
        }
        return null;
      } catch (error) {
        console.error('获取URL参数失败:', error);
        return null;
      }
    },

    // 新增：检测是否在APP环境中
    checkAppEnvironment() {
      const userAgent = navigator.userAgent.toLowerCase();
      return /rongyueapp/.test(userAgent); // 假设APP的WebView中包含"rongyueapp"标识
    },

    // 创建ai绘画
    async getAINewDialog() {
      try {
        const response = await chatApi.createAiDialog({
          tokens: cookies("token"),
          platform: 1,
        });
        this.newDialogSessonId = response.data.session;
      } catch (error) {
        console.error("创建AI绘画失败:", error);
      }
    },
    handleKeyDown(e) {
      if (e.key === "Enter") {
        if (!e.shiftKey && !this.isAiResponding) {
          e.preventDefault();
          this.sendMessage();
        }
      }
    },
    formatMessage(content) {
      if (!content) return "";
      // 对用户输入进行转义，防止 XSS 攻击
      const escapedContent = content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
      return marked(escapedContent);
    },
    toggleHistory() {
      this.showHistory = !this.showHistory;
    },
    async streamErrorMessage(errorMessage) {
      const fullErrorText = `抱歉，发生了错误：${errorMessage || "未知错误"}`;
      this.currentMessage.thinking = "";
      this.currentMessage.content = "";
      this.currentMessage.status = "error";

      // 逐字显示错误信息
      for (let i = 0; i < fullErrorText.length; i++) {
        this.currentMessage.content += fullErrorText[i];
        // 每个字符之间添加小延迟
        await new Promise((resolve) => setTimeout(resolve, 50));
        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    async processStreamQueue() {
      if (this.isProcessingStream || this.streamQueue.length === 0) return;

      this.isProcessingStream = true;
      const item = this.streamQueue[0];
      try {
        if (item.type === "thinking") {
          const formattedContent = this.formatMessage(item.content);
          this.$set(this.messages[item.messageIndex], "thinking", item.content);
          this.$set(
            this.messages[item.messageIndex],
            "formattedThinking",
            formattedContent
          );
        } else {
          const formattedContent = this.formatMessage(item.content);
          this.$set(this.messages[item.messageIndex], "content", item.content);
          this.$set(
            this.messages[item.messageIndex],
            "formattedContent",
            formattedContent
          );
        }

        // 添加小延迟以实现流式效果
        await new Promise((resolve) => setTimeout(resolve, 50));
      } catch (error) {
        console.error("处理消息队列时出错:", error);
      }

      this.streamQueue.shift();
      this.isProcessingStream = false;
      this.processStreamQueue();
    },

    async formatMessageWithDelay(content) {
      if (!content) return "";
      return this.formatMessage(content);
    },

    async sendMessage() {
      if (!this.inputMessage.trim()) return;
      this.shouldStopResponse = false;

      // 确保有会话ID
      if (!this.newDialogSessonId) {
        try {
          await this.getAINewDialog();
        } catch (error) {
          this.$message.error("创建会话失败，请重试");
          return;
        }
      }

      // 创建新的 AbortController
      this.abortController = new AbortController();

      // 检查是否已经在之前的用户消息中显示过相同的图片
      const hasShownImage = this.messages.some(
        (msg) => msg.type === "user" && msg.image === this.uploadedImage
      );

      const userMessage = {
        type: "user",
        content: this.inputMessage,
        formattedContent: this.formatMessage(this.inputMessage),
        image: !hasShownImage && this.uploadedImage ? this.uploadedImage : null, // 只在之前没有显示过该图片时显示
        hasImage: !hasShownImage && !!this.uploadedImage, // 只在之前没有显示过该图片时设置为true
      };
      this.messages.push(userMessage);

      // 创建新的消息对象用于流式显示
      this.currentMessage = {
        type: "assistant",
        content: "",
        thinking: "",
        formattedThinking: "",
        formattedContent: "",
        status: "thinking",
      };
      this.messages.push(this.currentMessage);

      // 设置AI正在响应状态
      this.isAiResponding = true;

      // 立即滚动到底部显示思考中的提示
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      try {
        const messageIterator = await chatApi.sendMessage_BE({
          keyword: this.inputMessage,
          tokens: cookies("token"),
          session: this.newDialogSessonId,
          signal: this.abortController.signal,
          image_name: this.send_image_name || "",
          is_image: this.send_image_name ? "on" : "off",
        });

        for await (const chunk of messageIterator) {
          if (this.shouldStopResponse) break;

          // 只有在有thinking字段时才显示思考状态
          if (chunk.thinking) {
            this.currentMessage.thinking = chunk.thinking;
            this.currentMessage.formattedThinking = this.formatMessage(
              chunk.thinking
            );
          }

          if (chunk.content) {
            this.currentMessage.content = chunk.content;
            this.currentMessage.formattedContent = this.formatMessage(
              chunk.content
            );

            // 更新状态
            if (chunk.status === "complete") {
              this.currentMessage.status = "complete";
            } else if (chunk.status === "answering") {
              this.currentMessage.status = "answering";
            } else if (chunk.status === "error") {
              this.currentMessage.status = "error";
            }
          }

          // 滚动到底部
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        // 判断是否是由于手动中断导致的错误
        if (error.name === "AbortError") {
          if (this.currentMessage) {
            this.currentMessage.status = "complete";
          }
        } else {
          await this.streamErrorMessage(
            error.message || "服务器错误，请稍后重试"
          );
        }
        console.error("发送消息失败:", error);
      } finally {
        // 无论成功失败都重置AI响应状态
        this.isAiResponding = false;
        this.shouldStopResponse = false;
        this.abortController = null;
      }

      this.inputMessage = "";
      this.currentMessage = null;
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    startNewChat() {
      if (this.messages.length === 0) {
        return;
      }
      this.$confirm("确定要开始新的对话吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          // 清空消息
          this.messages = [];
          this.inputMessage = "";
          this.stopAiResponse();
          // 重置会话ID和分页信息
          this.newDialogSessonId = "";
          this.currentHistorySession = "";
          this.selectedHistoryId = null;
          this.historyDetailPageNum = 1;
          this.historyDetailHasMore = true;
          this.pageNum = 1;
          this.hasMore = true;
          // 清理图片状态
          this.removeUploadedImage();
          // 重新获取历史记录
          await this.getHistoryList();
        })
        .catch(() => {
          console.log("用户取消新建对话");
        });
    },
    selectHistory(item) {
      if (!item || !item.session) {
        this.$message.warning("无效的历史记录");
        return;
      }

      this.selectedHistoryId = item.id;
      this.currentHistorySession = item.session;
      this.getHistoryDetailInfo(item.session);
    },
    async getHistoryList(isLoadMore = false) {
      if (!this.hasMore || this.loading) return;

      try {
        this.loading = true;
        const params_send = {
          page_num: this.pageNum,
          page_count: this.pageSize,
          platform: 1,
          tokens: cookies("token"),
        };

        const response = await chatApi.getHistory(params_send);
        if (response.data.pages && response.data.pages.length > 0) {
          const combinedArray = response.data.pages.map((item) => {
            const detail = response.params[item.id];
            return {
              ...item,
              ...detail,
            };
          });

          // 判断是否还有更多数据
          this.hasMore = combinedArray.length === this.pageSize;

          if (isLoadMore) {
            this.historyList = [...this.historyList, ...combinedArray];
          } else {
            this.historyList = combinedArray;
          }
          this.pageNum++;
        } else {
          this.historyList = [];
        }
      } catch (error) {
        this.$message.error("获取历史记录失败");
      } finally {
        this.loading = false;
        this.isFirstLoad = false;
      }
    },
    // 新增滚动加载方法
    handleHistoryScroll() {
      const historyList = this.$refs.historyListRef;
      if (!historyList) return;

      const { scrollTop, scrollHeight, clientHeight } = historyList;
      // 当距离底部20px时触发加载
      if (
        scrollHeight - scrollTop - clientHeight < 20 &&
        !this.loading &&
        this.hasMore
      ) {
        this.getHistoryList(true);
      }
    },
    // 获取历史记录详情
    async getHistoryDetailInfo(session, isLoadMore = false) {
      if (!isLoadMore) {
        // 如果不是加载更多，重置分页参数
        this.historyDetailPageNum = 1;
        this.historyDetailHasMore = true;
        this.messages = [];
      }

      if (!this.historyDetailHasMore || this.historyDetailLoading) return;

      try {
        this.historyDetailLoading = true;
        const response = await chatApi.getHistoryDetail({
          session: session,
          page_num: this.historyDetailPageNum,
          page_count: this.historyDetailPageSize,
          platform: 1,
          tokens: cookies("token"),
        });

        // 检查返回的数据是否为空
        if (!response.data.pages || response.data.pages.length === 0) {
          this.historyDetailHasMore = false;
          if (!isLoadMore) {
            this.$message.warning("暂无历史对话记录");
          }
          return;
        }

        // 将历史记录转换为消息格式，并整合图片路径
        const historyMessages = response.data.pages.map((msg) => {
          const messageData = {
            type: msg.role === 1 ? "user" : "assistant",
            content: msg.content,
            formattedContent: this.formatMessage(msg.content),
            thinking: msg.reasoning_content || "",
            formattedThinking: msg.reasoning_content
              ? this.formatMessage(msg.reasoning_content)
              : "",
            status: "complete",
          };

          // 如果是用户消息，且在 params 中存在对应的图片路径
          if (
            msg.role === 1 &&
            response.params &&
            response.params[msg.id] &&
            response.params[msg.id].path
          ) {
            messageData.hasImage = true;
            messageData.image = response.params[msg.id].path;
          }

          return messageData;
        });

        // 判断是否还有更多数据
        this.historyDetailHasMore =
          historyMessages.length === this.historyDetailPageSize;

        if (isLoadMore) {
          // 加载更多时，将新消息添加到数组前面
          this.messages = [...historyMessages, ...this.messages];
        } else {
          this.messages = historyMessages;
        }

        // 更新分页和会话信息
        this.historyDetailPageNum++;
        this.currentHistorySession = session;
        this.newDialogSessonId = session;

        // 如果不是加载更多，滚动到底部
        if (!isLoadMore) {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("获取历史记录详情失败:", error);
        this.historyDetailHasMore = false;
        if (!isLoadMore) {
          this.$message.error("获取历史记录详情失败，请稍后重试");
        }
        this.loadingError = true;
      } finally {
        this.historyDetailLoading = false;
      }
    },
    // 处理消息区域的滚动
    handleMessagesScroll() {
      if (this.scrollThrottleTimer) return;

      this.scrollThrottleTimer = setTimeout(() => {
        const container = this.$refs.messageContainer;
        if (!container) return;

        // 当滚动到顶部附近时触发加载更多
        if (
          container.scrollTop < 50 &&
          !this.historyDetailLoading &&
          this.historyDetailHasMore &&
          this.currentHistorySession
        ) {
          // 记录当前滚动位置
          const oldScrollHeight = container.scrollHeight;

          // 加载更多历史消息
          this.getHistoryDetailInfo(this.currentHistorySession, true).then(
            () => {
              // 加载完成后，保持滚动位置
              this.$nextTick(() => {
                const newScrollHeight = container.scrollHeight;
                container.scrollTop = newScrollHeight - oldScrollHeight;
              });
            }
          );
        }

        this.scrollThrottleTimer = null;
      }, 200); // 200ms 的节流时间
    },
    stopAiResponse() {
      this.shouldStopResponse = true;
      if (this.abortController) {
        this.abortController.abort();
      }
    },
    handleModeChange(mode) {
      if (mode === "knowledge") {
        this.$router.push("/ai-knowledge-base");
      }
    },
    isDesktop() {
      return window.innerWidth > 768;
    },
    async deleteHistory(item) {
      try {
        await this.$confirm("确定要删除这条历史记录吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
          customClass: "custom-message-box",
          distinguishCancelAndClose: true,
          closeOnClickModal: true,
          closeOnPressEscape: true,
          showClose: true,
          center: true,
          roundButton: true,
        });

        // 添加到删除集合中
        this.deletingItems.add(item.id);

        try {
          const response = await chatApi.updateStatus({
            session: item.session,
            tokens: cookies("token"),
          });

          if (response.code === 200) {
            // 等待动画完成
            await new Promise((resolve) => setTimeout(resolve, 300));

            this.$message.success("删除成功");
            // 如果删除的是当前选中的会话，清空右侧内容
            if (item.id === this.selectedHistoryId) {
              this.messages = [];
              this.selectedHistoryId = null;
              this.currentHistorySession = "";
              this.newDialogSessonId = "";
            }
            // 重置分页相关数据
            this.pageNum = 1;
            this.hasMore = true;
            this.historyList = [];
            // 重新获取历史记录
            await this.getHistoryList();
          } else {
            this.$message.error("删除失败");
          }
        } finally {
          // 从删除集合中移除
          this.deletingItems.delete(item.id);
        }
      } catch (error) {
        // 用户取消删除操作，不做任何处理
        if (error === "cancel") {
          return;
        }
        this.$message.error("删除操作失败");
      }
    },
    // 处理图片上传
    async handleImageUpload(options) {
      const file = options.file;
      // 检查文件类型
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        this.$message.error("请上传图片文件！");
        return;
      }

      // 检查文件大小（限制为 5MB）
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error("图片大小不能超过 5MB！");
        return;
      }

      try {
        // 调用上传接口
        const response = await chatApi.uploadImage({
          file: file,
          tokens: cookies("token"),
        });

        if (response.code === 200) {
          // 保存图片URL用于预览
          this.uploadedImage = response.data.image_url;
          this.send_image_name = response.data.image_name;
          options.onSuccess(response);
        } else {
          throw new Error(response.message || "上传失败");
        }
      } catch (error) {
        this.$message.error("图片上传失败，请重试！");
        this.uploadedImage = null;
        options.onError(error);
      }
    },
    // 新增：移除已上传的图片
    removeUploadedImage() {
      if (this.uploadedImage) {
        URL.revokeObjectURL(this.uploadedImage);
        this.uploadedImage = null;
        this.send_image_name = null;
      }
    },
    // 新增：处理图片点击预览
    handlePreviewClick() {
      this.showImagePreview = true;
    },
    // 添加处理图片点击的方法
    handleImageClick(imageUrl) {
      this.uploadedImage = imageUrl;
      this.showImagePreview = true;
    },
  },
  created() {
    // 1. 优先获取URL中的token（支持Hash路由）
    const urlToken = this.getUrlParam('token');
    if (urlToken) {
      // 将token存储到cookie中
      document.cookie = `token=${urlToken}; path=/`;
      this.getHistoryList();
      return;
    }

    // 2. 检查cookie中是否有token
    const cookieToken = cookies("token");
    if (cookieToken) {
      this.getHistoryList();
      return;
    }

    // 3. 判断当前环境
    const userAgent = navigator.userAgent.toLowerCase();
    const isApp = /rongyueapp/.test(userAgent);
    const isMobile = /iphone|ipad|ipod|android/.test(userAgent);

    // 4. 根据环境处理未登录情况
    if (isApp) {
      this.$message.error('未获取到有效的登录信息');
    } else if (isMobile) {
      // H5环境未登录
      window.location.href = "https://www.rongyuejiaoyu.com/mobile/user/login.html";
    } else {
      // PC环境未登录
      window.location.href = "https://www.rongyuejiaoyu.com/index/user/login.html";
    }
  },
  mounted() {
    // 监听窗口大小变化
    window.addEventListener("resize", () => {
      this.showHistory = this.isDesktop();
    });

    // 在移动端禁用缩放
    if (window.innerWidth <= 768) {
      disableZoom();
    }
  },
  beforeDestroy() {
    // 清除定时器
    if (this.scrollThrottleTimer) {
      clearTimeout(this.scrollThrottleTimer);
    }
    // 移除事件监听器
    window.removeEventListener("resize", () => {
      this.showHistory = this.isDesktop();
    });
    // 清理图片URL
    this.removeUploadedImage();
  },
};
</script>

<style lang="scss" scoped>
.chat-container {
  display: flex;
  height: 100vh;
  background-color: #f5f5f5;
}

.history-panel {
  width: 260px;
  background-color: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);

  &.history-hidden {
    width: 0;
    overflow: hidden;
  }

  .history-header {
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e8e8e8;
    background-color: #fafafa;

    i {
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.3s;

      &:hover {
        color: $primary-color;
        background-color: rgba(64, 158, 255, 0.1);
      }
    }
  }

  .history-list {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    position: relative;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch; // 添加弹性滚动

    .history-items-container {
      will-change: contents; // 优化渲染性能
      transform: translateZ(0); // 启用GPU加速
    }

    .loading-status-container {
      position: relative;
      min-height: 50px;
      margin-top: 8px;
    }

    .loading-trigger {
      position: absolute;
      bottom: 50px;
      left: 0;
      right: 0;
      height: 20px;
      pointer-events: none;
    }

    .history-item {
      padding: 12px;
      cursor: pointer;
      border-radius: 6px;
      margin-bottom: 8px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      background-color: #fff;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      border: 2px solid transparent;
      opacity: 0;
      animation: fadeInUp 0.3s forwards;
      will-change: transform, opacity;
      backface-visibility: hidden;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      position: relative;

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: #f5f7fa;
          transform: translateX(4px);
        }
      }

      // 移动端激活状态
      @media (hover: none) {
        &:active {
          background-color: #f5f7fa;
          transform: translateX(4px);
        }
      }

      @for $i from 1 through 10 {
        &:nth-child(#{$i}) {
          animation-delay: #{$i * 0.1}s;
        }
      }

      &-active {
        border-color: $primary-color;
        background-color: rgba($primary-color, 0.05);
        transform: translateX(4px);

        .delete-icon {
          opacity: 1;
          pointer-events: auto;
        }
      }

      .history-logo {
        width: 24px;
        height: 24px;
        margin-right: 12px;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .history-content {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: $text-regular;
        font-size: 14px;
      }

      .delete-icon {
        margin-left: 12px;
        color: #909399;
        font-size: 16px;
        opacity: 0;
        transition: all 0.3s;
        padding: 8px;
        margin: -8px;
        box-sizing: content-box;
        position: relative;
        z-index: 2;
        pointer-events: none;

        &:hover {
          color: $danger-color;
        }

        &.deleting {
          pointer-events: none;
          color: $primary-color;
          animation: rotating 1s linear infinite;
        }
      }

      .delete-icon {
        padding: 12px; // 增大点击区域
        margin: -12px;
      }

      &-active {
        .delete-icon {
          opacity: 1;
          pointer-events: auto;
        }
      }

      &.deleting {
        opacity: 0.5;
        transform: translateX(100%);
        transition: all 0.3s ease;
      }
    }

    .loading-more {
      text-align: center;
      padding: 15px 0;
      color: $text-secondary;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(4px);

      &.error {
        color: $danger-color;
        background: rgba(245, 108, 108, 0.1);

        i {
          color: $danger-color;
        }

        .el-button--text {
          color: $primary-color;
          padding: 0 4px;
          font-weight: 500;

          &:hover {
            color: mix($primary-color, black, 80%);
          }
        }
      }

      i {
        margin-right: 8px;
        font-size: 16px;
      }
    }

    .no-more {
      text-align: center;
      padding: 15px 0;
      color: $text-secondary;
      font-size: 14px;
      opacity: 0.8;
    }

    .empty-history {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 0;
      color: $text-secondary;

      i {
        font-size: 48px;
        margin-bottom: 16px;
        color: #c0c4cc;
      }

      p {
        font-size: 14px;
        margin: 0;
      }
    }
  }

  .new-chat {
    padding: 16px;
    text-align: center;
    cursor: pointer;
    border-top: 1px solid #e8e8e8;
    color: $primary-color;
    transition: all 0.3s;

    &:hover {
      background-color: rgba(64, 158, 255, 0.1);
    }

    i {
      margin-right: 8px;
    }
  }
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  position: relative;

  &.full-width {
    margin-left: 0;
  }
}

.chat-header {
  padding: 12px 24px;
  border-bottom: 1px solid #e8e8e8;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-left {
    display: flex;
    align-items: center;
  }

  .header-right {
    display: flex;
    align-items: center;
  }

  .toggle-history-btn {
    display: flex;
    align-items: center;
    color: $primary-color;
    padding: 8px 16px;
    border-radius: 4px;
    transition: all 0.3s;

    &:hover {
      background-color: rgba(64, 158, 255, 0.1);
    }

    i {
      margin-right: 8px;
      font-size: 16px;
    }
  }

  ::v-deep .el-switch {
    margin-left: 16px;

    .el-switch__label {
      color: #606266;
      font-size: 14px;

      &.is-active {
        color: $primary-color;
      }
    }
  }

  // 添加移动端样式
  @media screen and (max-width: 768px) {
    padding: 12px 16px;
    
    .toggle-history-btn {
      padding: 6px 12px;
      margin-right: 8px;
      
      i {
        margin-right: 4px;
        font-size: 14px;
      }
    }

    ::v-deep .el-switch {
      margin-left: 8px;
      
      .el-switch__label {
        font-size: 12px;
        
        &.is-active {
          font-size: 12px;
        }
        
        // 缩小文字间距
        &--left {
          margin-right: 4px;
        }
        
        &--right {
          margin-left: 4px;
        }
      }
    }
  }

  // 超小屏幕适配
  @media screen and (max-width: 360px) {
    padding: 8px 12px;
    
    .toggle-history-btn {
      padding: 4px 8px;
      font-size: 12px;
      
      i {
        font-size: 12px;
      }
    }

    ::v-deep .el-switch {
      transform: scale(0.9);
      margin-left: 4px;
      
      .el-switch__label {
        font-size: 11px;
        
        &.is-active {
          font-size: 11px;
        }
      }
    }
  }
}

.welcome-page {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);

  .welcome-content {
    max-width: min(600px, 90%);
    width: 100%;
    text-align: center;
    padding: clamp(20px, 5vw, 40px);
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);

    @media screen and (max-width: 768px) {
      background: #ffffff;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
    }

    .logo {
      width: clamp(60px, 10vw, 80px);
      height: clamp(60px, 10vw, 80px);
      margin-bottom: clamp(16px, 4vw, 24px);
      filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }
    }

    h1 {
      font-size: clamp(20px, 4vw, 24px);
      font-weight: 600;
      margin-bottom: clamp(12px, 3vw, 16px);
      color: $text-primary;

      .highlight {
        color: $primary-color;
        position: relative;

        &::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 2px;
          background: $primary-color;
          opacity: 0.3;
        }
      }
    }

    .description {
      font-size: clamp(14px, 3vw, 16px);
      color: $text-regular;
      margin-bottom: clamp(24px, 6vw, 32px);
      line-height: 1.6;
      opacity: 0.8;
    }
  }
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: clamp(16px, 4vw, 24px);
  background-color: #fff;

  .message-item {
    display: flex;
    margin-bottom: clamp(16px, 4vw, 24px);
    animation: fadeIn 0.3s ease;
    max-width: 900px;
    margin-left: auto;
    margin-right: auto;

    .avatar {
      width: clamp(32px, 8vw, 40px);
      height: clamp(32px, 8vw, 40px);
      margin-right: clamp(12px, 3vw, 16px);
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }

    .message-content {
      max-width: min(80%, 800px);
      padding: clamp(12px, 3vw, 16px);
      border-radius: 8px;
      background-color: #f5f7fa;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      line-height: 1.6;
      font-size: clamp(13px, 2.5vw, 14px);
      color: $text-regular;
      white-space: pre-wrap;
      word-break: break-word;
      transition: all 0.3s ease;

      ::v-deep {
        .custom-heading {
          margin: 1.5em 0 0.8em;
          font-weight: 600;
          line-height: 1.4;
          color: $text-primary;
          border-bottom: 1px solid $border-color-light;
          padding-bottom: 0.3em;

          &-1 {
            font-size: 1.5em;
          }
          &-2 {
            font-size: 1.3em;
          }
          &-3 {
            font-size: 1.2em;
          }
          &-4 {
            font-size: 1.1em;
          }
        }

        .custom-paragraph {
          margin: 0.8em 0;
          line-height: 1.8;
          text-align: justify;

          &:not(:first-child) {
            margin-left: 1.5em; // 非第一段落缩进
          }
        }

        .custom-list {
          margin: 0.8em 0;
          padding-left: 2em;

          .custom-list-item {
            margin: 0.5em 0;
            line-height: 1.6;
            position: relative;
          }
        }

        ul,
        ol {
          padding-left: 2em;
          margin: 0.8em 0;
        }

        li {
          margin: 0.5em 0;
        }
      }

      .thinking-content,
      .answer-content {
        transition: all 0.3s ease;
      }

      .thinking-content {
        opacity: 0.7;
        color: $text-secondary;
        font-style: italic;
        padding-bottom: 8px;
        margin-bottom: 8px;
        border-bottom: 1px dashed #e8e8e8;
        animation: thinking 1.5s infinite;
      }

      .answer-content {
        color: $text-primary;

        &.error {
          color: $danger-color;
          background-color: rgba($danger-color, 0.1);
          padding: 8px 12px;
          border-radius: 6px;
          border-left: 4px solid $danger-color;
        }
      }

      code {
        background-color: #f8f8f8;
        padding: 2px 4px;
        border-radius: 4px;
        color: #e83e8c;
        font-family: monospace;
        font-size: 0.9em;
      }

      pre {
        background-color: #282c34;
        padding: clamp(12px, 3vw, 16px);
        border-radius: 8px;
        margin: 8px 0;
        overflow-x: auto;
        font-size: 0.9em;

        code {
          background-color: transparent;
          color: #abb2bf;
          padding: 0;
        }
      }

      .message-image {
        margin-bottom: 8px;

        .sent-image {
          max-width: 200px;
          max-height: 200px;
          border-radius: 8px;
          cursor: pointer;
          transition: transform 0.3s ease;

          &:hover {
            transform: scale(1.02);
          }
        }
      }
    }

    &.user {
      flex-direction: row-reverse;

      .avatar {
        margin-right: 0;
        margin-left: clamp(12px, 3vw, 16px);
      }

      .message-content {
        background-color: #e3f2fd;
        color: $text-primary;
      }
    }
  }
}

.input-area {
  padding: clamp(16px, 4vw, 24px);
  background-color: #fff;
  border-top: 1px solid #e8e8e8;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);

  .textarea-wrapper {
    position: relative;
    margin-bottom: clamp(12px, 3vw, 16px);
    border-radius: 8px;
    border: 1px solid #dcdfe6;
    overflow: hidden;
    max-width: 900px;
    margin: 0 auto;

    .preview-area {
      padding: 8px;
      background-color: #f8f9fa;
      border-bottom: 1px solid #dcdfe6;

      .image-preview {
        position: relative;
        display: inline-block;
        width: 50px;
        height: 50px;
        margin: 4px;
        cursor: pointer;

        img {
          width: 100%;
          height: 100%;
          border-radius: 4px;
          display: block;
          object-fit: cover;
        }

        .remove-image {
          position: absolute;
          top: -6px;
          right: -6px;
          background-color: rgba(0, 0, 0, 0.5);
          color: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 1;

          &:hover {
            background-color: rgba(0, 0, 0, 0.7);
          }

          i {
            font-size: 10px;
          }
        }
      }
    }

    .el-textarea {
      width: 100%;

      ::v-deep .el-textarea__inner {
        resize: none;
        border: none;
        border-radius: 0;
        padding: 12px;
        padding-right: 40px;
        line-height: 1.6;
        font-size: clamp(13px, 2.5vw, 14px);
        min-height: 60px;

        &:focus {
          box-shadow: none;
        }
      }
    }

    .input-actions {
      position: absolute;
      right: 8px;
      bottom: 8px;
      z-index: 2;
      display: flex;
      align-items: center;
      gap: 4px;
      background-color: #fff;
      border-radius: 4px;
      padding: 0 2px;

      .action-divider {
        width: 1px;
        height: 20px;
        background: linear-gradient(
          to bottom,
          transparent,
          #dcdfe6 20%,
          #dcdfe6 80%,
          transparent
        );
        margin: 0 2px;
      }

      .upload-image {
        ::v-deep .el-button {
          padding: 8px;
          margin: 0;
          color: #909399;

          &:hover {
            color: $primary-color;
          }

          i {
            font-size: 20px;
          }
        }
      }

      .send-button {
        padding: 6px;
        margin: 0;
        color: #fff;
        transition: all 0.3s ease;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #909399;

        &:not([disabled]) {
          background-color: $primary-color;

          &:hover {
            transform: scale(1.1);
            background-color: mix($primary-color, white, 90%);
          }
        }

        &[disabled] {
          background-color: #909399;
          cursor: not-allowed;
          opacity: 0.8;
        }

        i {
          font-size: 16px;
          margin: 0;
        }
      }
      .stop-button {
        width: 36px;
        height: 36px;
        padding: 0;
        border: none;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f56c6c;
        transition: all 0.3s ease;

        &:hover {
          background-color: darken(#f56c6c, 10%);
          transform: scale(1.05);
        }

        i {
          font-size: 18px;
          color: #fff;
        }
      }
    }
  }

  .button-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    .left-buttons {
      flex: 1;
      display: flex;
      align-items: center;

      .upload-image {
        ::v-deep .el-button {
          padding: 8px;
          margin: 0;

          &:hover {
            color: $primary-color;
          }

          i {
            font-size: 20px;
          }
        }
      }
    }

    .center-buttons {
      display: flex;
      justify-content: center;
      flex: 1;
    }

    .right-buttons {
      display: flex;
      justify-content: flex-end;
      flex: 1;
    }

    .el-button--text {
      color: $text-secondary;
      padding: 8px;

      &:hover {
        color: $danger-color;
      }

      &[disabled] {
        color: $text-placeholder;
      }
    }

    .el-button--primary {
      padding: clamp(8px, 2vw, 12px) clamp(16px, 4vw, 32px);
      font-size: clamp(13px, 2.5vw, 14px);
      border-radius: 6px;
      min-width: 80px;
    }
  }
}

// 响应式设计
@media screen and (max-width: 768px) {
  .history-panel {
    position: fixed;
    left: 0;
    top: 0;
    height: 100%;
    z-index: 1000;
    background-color: #fff;
    transform: translateX(-100%);
    transition: transform 0.3s ease;

    &:not(.history-hidden) {
      transform: translateX(0);
    }
  }

  .chat-main {
    width: 100%;
    margin-left: 0;
  }

  .message-item {
    .message-content {
      max-width: 85% !important;
    }
  }

  .input-area {
    padding: 12px;

    .textarea-wrapper {
      .el-textarea {
        ::v-deep .el-textarea__inner {
          padding: 12px;
        }
      }
    }
  }
}

// 暗色模式支持
@media (prefers-color-scheme: dark) {
  .chat-container {
    background-color: #1a1a1a;
  }

  .welcome-page {
    background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);

    .welcome-content {
      background: rgba(40, 40, 40, 0.9);
    }
  }

  .message-content {
    background-color: #2a2a2a !important;
    color: #e0e0e0 !important;

    code {
      background-color: #333 !important;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes thinking {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 添加旋转动画
@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
<style lang="scss">
// 全局样式，覆盖 Element UI 的对话框样式
.el-message-box {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 90% !important;
  z-index: 10000 !important;
}

// 添加图片预览弹窗样式
.image-preview-dialog {
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
  position: fixed;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  background: transparent !important;
  box-shadow: none !important;

  .el-dialog__header {
    padding: 0;
    position: absolute;
    right: 20px;
    top: 20px;
    z-index: 2100;

    .el-dialog__headerbtn {
      position: static;
      font-size: 20px;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 50%;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.7);
      }

      .el-dialog__close {
        color: white;
      }
    }
  }

  .el-dialog__body {
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.75);

    .preview-image {
      max-width: 90vw;
      max-height: 90vh;
      object-fit: contain;
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
    }
  }
}
</style>
